import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  DataUploadApi,
  IApiError,
  IDataUploadLogDto,
  normalizeErrors,
  SimpleResult,
  simpleResultErrorEmptyResponse,
} from "@ryde/shared";

export const useDataUploadsActions = () => {
  const { t } = useTranslation();

  /**
   * Queues one data upload. Success means the job reached the queue — the run itself finishes minutes
   * later and reports back by email, so the caller shows a "you'll be notified" toast, not an outcome.
   *
   * The endpoint never resolves with `success: false`: it returns success or throws — a closed schedule
   * is a 404, a revoked permission a 403. Those land in the `catch` and are normalized into an error
   * result carrying the backend's own message, so the caller can toast it rather than one generic line.
   * No shared page spinner here: a single row's run must not disable the whole grid (the run button owns
   * its own busy state), and `fetchLastRunLog` below is spinner-free for the same reason.
   */
  const executeDataUpload = useEvent(
    async (dataUploadId: number): Promise<SimpleResult<void>> => {
      try {
        const result = await DataUploadApi.execute(dataUploadId);

        if (!result) {
          return simpleResultErrorEmptyResponse(t);
        }

        return new SimpleResult(result);
      } catch (error) {
        return new SimpleResult({
          error: normalizeErrors(error as IApiError, t),
        });
      }
    },
  );

  /**
   * Fetches the last run's log (already filtered + redacted by the backend). Returns the result rather
   * than toasting so the caller decides — the viewer toasts the error and keeps its section retryable.
   * Like `execute`, a failure arrives as a thrown 404/403, caught and normalized here.
   */
  const fetchLastRunLog = useEvent(
    async (dataUploadId: number): Promise<SimpleResult<IDataUploadLogDto>> => {
      try {
        const result = await DataUploadApi.fetchLastRunLog(dataUploadId);

        if (!result) {
          return simpleResultErrorEmptyResponse(t);
        }

        return new SimpleResult(result);
      } catch (error) {
        return new SimpleResult({
          error: normalizeErrors(error as IApiError, t),
        });
      }
    },
  );

  return { executeDataUpload, fetchLastRunLog };
};
